import React, { useEffect, useState, useMemo } from "react";
import { connect } from "react-redux";
import { TileLayer } from "react-leaflet";

import MapActioner from "services/map/actioner";
import MapUtils from "services/map/utils";
import ParkStatisticsActioner from "services/parkStatistics/actioner";
import ContractUtils from "services/contract/utils";
import MapConfig from "services/map/MapConfig";
import ParkedCarsActioner from "services/parkedCars/actioner";
import AvailableParkingActioner from "services/availableParking/actioner";

import LeafletMap from "components/LeafletMap";
import AreasLayers from "components/AreasLayers";
import ParkedCarsLayers from "components/ParkedCarsLayers";
import AvailableParkingLayers from "components/AvailableParkingLayers";
import AislesLayers from "../AislesLayers";
import HeatMapLayers from "../HeatMapLayers";

function MapComponent({
  aislesDisplayed,
  customMarkers,
  parkStatisticsMap,
  center,
  contract,
  currentFilters,
  heatmapOccupancy,
  heatmapOccupancyConfig,
  initFloorsData,
  maxZoom,
  minZoom,
  onRecenter,
  parkedCars,
  availableParking,
  parkStatisticsDetails,
  retrieveLayersInfos,
  setConfigParam,
  tilesUrl,
  zoom,
  setSelectedLayers,
  isDisabled,
  retrieveAllStats,
  clearParkedCars,
  clearAvailableParking,
  currentFloor,
}) {
  const [leafletMap, setLeafletMap] = useState();
  const [prevFloor, setPrevFloor] = useState(currentFloor);

  useEffect(() => {
    async function initializeData() {
      if (parkStatisticsMap?.initialized) {
        initFloorsData();
        return null;
      }

      await initFloorsData();
      await retrieveLayersInfos();
      await retrieveAllStats();
    }

    initializeData();
  }, [
    parkStatisticsMap?.initialized,
    initFloorsData,
    retrieveLayersInfos,
    retrieveAllStats,
  ]);

  useEffect(() => {
    if (leafletMap) {
      onRecenter(leafletMap?.leafletElement);
    }
  }, [onRecenter, leafletMap]);

  useEffect(() => {
    async function resetMap() {
      if (currentFloor !== prevFloor) {
        await Promise.all([
          clearParkedCars(),
          clearAvailableParking(),
          setPrevFloor(currentFloor),
        ]);
      }
    }

    resetMap();
  }, [currentFloor, prevFloor, clearParkedCars, clearAvailableParking]);

  const toggleMarkersAndAreas = useMemo(() => {
    return MapUtils.toggleMarkersAndAreas(
      currentFilters?.timestamp,
      ContractUtils.getParkMaxDaysAllowed(contract)
    );
  }, [currentFilters?.timestamp, contract]);

  const toggleHeatmap = useMemo(() => {
    return MapUtils.toggleHeatmap(
      currentFilters?.timestamp,
      ContractUtils.getParkMaxDaysAllowed(contract)
    );
  }, [currentFilters?.timestamp, contract]);

  function getMapRadius(currentMap) {
    return MapUtils.getRadiusInMeters(
      currentMap.getBounds().getNorthEast(),
      currentMap.getCenter()
    );
  }

  function handleClosePopup() {
    return leafletMap?.leafletElement?.closePopup();
  }

  function handleZoomEnd(e) {
    const currentMap = e?.target;
    handleClosePopup();
    setConfigParam("currentRadius", getMapRadius(currentMap));
    setConfigParam("currentZoom", currentMap.getZoom());
  }

  function handleMoveEnd(e) {
    const currentMap = e?.target;
    handleClosePopup();
    setConfigParam("currentRadius", getMapRadius(currentMap));
    setConfigParam("currentPosition", currentMap.getCenter());
  }

  function handleOnLoad(e) {
    const currentMap = e?.target;
    setConfigParam("currentRadius", getMapRadius(currentMap._map));
    setConfigParam("currentPosition", currentMap._map.getCenter());
  }

  function handleGetLayerLabel(layer) {
    if (layer.type === "point" || layer.type === "area") {
      return layer?.short_name || layer?.long_name || null;
    }
    return null;
  }

  function handleGetAreaLabelIcon(layer) {
    return MapConfig?.iconByType?.park?.[layer.type]?.[layer.apiType];
  }

  return (
    <LeafletMap
      preferCanvas
      style={{
        width: "100%",
        height: "100%",
      }}
      setMapRef={setLeafletMap}
      center={center}
      zoom={zoom}
      minZoom={minZoom}
      maxZoom={maxZoom}
      onZoomEnd={handleZoomEnd}
      onMoveEnd={handleMoveEnd}
      isDisabled={isDisabled}
    >
      <TileLayer url={MapConfig.defaultTileUrl} />
      <TileLayer
        url={tilesUrl}
        onLoad={handleOnLoad}
        minZoom={minZoom}
        maxZoom={maxZoom}
      />
      {toggleMarkersAndAreas && (
        <AreasLayers
          map={parkStatisticsMap}
          options={parkStatisticsDetails}
          onSelectLayers={setSelectedLayers}
          getLabel={handleGetLayerLabel}
          getLabelIcon={handleGetAreaLabelIcon}
        />
      )}
      {!toggleHeatmap && aislesDisplayed && <AislesLayers />}
      {toggleMarkersAndAreas && (
        <AvailableParkingLayers
          cars={availableParking}
          options={{
            ...customMarkers,
            timestamp: currentFilters?.timestamp,
            zoom,
          }}
        />
      )}
      {toggleMarkersAndAreas && (
        <ParkedCarsLayers
          cars={parkedCars}
          options={{
            ...customMarkers,
            timestamp: currentFilters?.timestamp,
            zoom,
          }}
        />
      )}
      {toggleHeatmap && (
        <HeatMapLayers
          leafletMap={leafletMap}
          points={heatmapOccupancy?.items}
          options={heatmapOccupancyConfig}
        />
      )}
    </LeafletMap>
  );
}

const mapStateToProps = (state) => {
  return {
    aislesDisplayed: state.Map?.config?.aislesDisplayed,
    customMarkers: state.Map?.config?.custom_markers,
    center: MapUtils.getDefaultCenter(state.Map),
    contract: state.Contract.contract,
    currentFilters: state.ParkStatistics.filters,
    maxZoom: MapUtils.getMaxZoom(state.Map),
    minZoom: MapUtils.getMinZoom(state.Map),
    parkStatisticsMap: state.ParkStatistics.map,
    parkStatisticsDetails: state.ParkStatistics.details,
    tilesUrl: MapUtils.getTilesUrl(state.Map),
    zoom: MapUtils.getZoom(state.Map),
    heatmapOccupancy: state.HeatmapOccupancy.list,
    heatmapOccupancyConfig: MapUtils.getHeatmapOccupancy(state.Map),
    parkedCars: state.ParkedCars.list,
    availableParking: state.AvailableParking.list,
    isDisabled: state.Map?.config?.mapDisabled,
    currentFloor: MapUtils.getFloor(state.Map),
  };
};

const mapDispatchToProps = (dispatch) => ({
  retrieveLayersInfos: () =>
    dispatch(ParkStatisticsActioner.retrieveLayersInfos()),
  setConfigParam: (key, value) =>
    dispatch(MapActioner.setConfigParam(key, value)),
  setSelectedLayers: (layers) =>
    dispatch(ParkStatisticsActioner.setSelectedLayers(layers)),
  initFloorsData: () =>
    dispatch(MapActioner.initFloorsDataFromModule("park_stats")),
  retrieveAllStats: () => dispatch(ParkStatisticsActioner.retrieveAllStats()),
  clearParkedCars: () => dispatch(ParkedCarsActioner.setList([])),
  clearAvailableParking: () => dispatch(AvailableParkingActioner.setList([])),
});

export default connect(mapStateToProps, mapDispatchToProps)(MapComponent);
